<template>
  <div class="bg-white rounded-lg shadow-sm border border-gray-200 lg:col-span-2">
    <div class="p-4 border-b border-gray-200 flex items-center justify-between">
      <div class="flex items-center space-x-3">
        <span class="material-symbols-outlined text-xl text-primary">receipt_long</span>
        <h2 class="text-lg font-semibold text-gray-800">Payment History</h2>
      </div>
      <router-link 
        to="/payment-history"
        class="inline-flex items-center px-3 py-1.5 border border-primary text-sm font-medium rounded-lg shadow-sm text-primary bg-white hover:bg-primary-lighter transition-colors"
      >
        View All
        <span class="material-symbols-outlined text-sm ml-1">arrow_forward</span>
      </router-link>
    </div>
    
    <div class="p-6">
      <div v-if="loading" class="space-y-2">
        <div v-for="n in 3" :key="n" class="animate-pulse h-8 bg-gray-100 rounded mb-2"></div>
      </div>
      
      <div v-else>
        <div v-if="payments.length === 0" class="text-center py-8">
          <div class="flex-shrink-0 w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center mx-auto mb-4">
            <span class="material-symbols-outlined text-4xl text-gray-400">receipt_long</span>
          </div>
          <p class="text-lg font-medium text-gray-600 mb-2">No Payment History</p>
          <p class="text-sm text-gray-500 mb-4">Your payment transactions will appear here once you make a purchase.</p>
          <router-link 
            to="/payment-history"
            class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
          >
            <span class="material-symbols-outlined text-sm mr-2">receipt_long</span>
            View Payment History
          </router-link>
        </div>
        
        <div v-else class="space-y-4">
          <div v-for="payment in payments" :key="payment.id" class="flex items-center justify-between border-b border-gray-100 pb-4 mb-4 last:border-b-0 last:mb-0 last:pb-0">
            <div class="flex items-center space-x-3">
              <span class="material-symbols-outlined text-xl"
                :class="getPaymentTypeClasses(payment.type)">
                {{ getPaymentIcon(payment.type) }}
              </span>
              <div>
                <div class="text-base font-medium text-gray-900">{{ getPaymentTitle(payment.type) }}</div>
                <div class="text-xs text-gray-500">{{ formatDate(payment.createdAt) }}</div>
                <div v-if="payment.productName" class="text-xs text-gray-400">{{ payment.productName }}</div>
              </div>
            </div>
            <div class="text-right">
              <div class="text-base font-bold text-gray-900">{{ formatCurrency(payment.amount, payment.currency) }}</div>
              <div class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium"
                   :class="getStatusClasses(payment.status)">
                {{ payment.status }}
              </div>
            </div>
          </div>
          
          <div class="text-center mt-4 pt-4 border-t border-gray-100">
            <router-link 
              to="/payment-history"
              class="inline-flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors"
            >
              <span class="material-symbols-outlined text-sm mr-2">receipt_long</span>
              View Full Payment History
            </router-link>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'PaymentHistoryCard',
  props: {
    payments: {
      type: Array,
      default: () => []
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },
    
    formatCurrency(amount, currency = 'USD') {
      if (typeof amount !== 'number' || isNaN(amount)) return '$0.00';
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase()
      }).format(amount);
    },
    
    getPaymentTitle(type) {
      if (type === 'subscription_created') return 'Subscription Created';
      if (type === 'subscription_payment_success') return 'Subscription Payment';
      if (type === 'subscription_payment_failed') return 'Payment Failed';
      if (type === 'subscription_cancelled') return 'Subscription Cancelled';
      return 'Payment';
    },
    
    getPaymentIcon(type) {
      if (type === 'subscription_created') return 'add_circle';
      if (type === 'subscription_payment_success') return 'payments';
      if (type === 'subscription_payment_failed') return 'error';
      if (type === 'subscription_cancelled') return 'cancel';
      return 'receipt';
    },
    
    getPaymentTypeClasses(type) {
      if (type === 'subscription_created') return 'text-blue-500';
      if (type === 'subscription_payment_success') return 'text-green-500';
      if (type === 'subscription_payment_failed') return 'text-red-500';
      if (type === 'subscription_cancelled') return 'text-gray-500';
      return 'text-primary';
    },
    
    getStatusClasses(status) {
      if (status === 'completed') return 'bg-green-100 text-green-800';
      if (status === 'failed') return 'bg-red-100 text-red-800';
      if (status === 'cancelled') return 'bg-gray-100 text-gray-800';
      return 'bg-blue-100 text-blue-800';
    }
  }
};
</script>

<style scoped>
.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
}
</style>
